/**
 * 页面状态统一渲染：加载中 / 加载失败 / 空数据
 *
 * 目标是「任何情况下都不出现白屏，也不出现转了天荒地老的圈」：
 *   - 加载中：骨架屏（表格用 Skeleton，不是空白）
 *   - 失败：Result + 具体原因 + traceId + 重试按钮
 *   - 空数据：Empty + 引导下一步动作
 *   - 演示数据：Alert 明确标注「后端接口未就绪，当前为演示数据」
 */

import { Alert, Button, Empty, Result, Skeleton, Space, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ApiCode } from '@ilc/shared';
import { ApiError } from '@/api/client';

export interface ErrorStateProps {
  /** 允许传 null：页面在「无数据且无错误明细」时也能给出可读提示，而不是白屏 */
  error: ApiError | null;
  onRetry?: () => void;
  title?: string;
}

export function ErrorState({ error, onRetry, title }: ErrorStateProps): React.ReactElement {
  const err =
    error ??
    new ApiError({ message: '数据加载失败，且未取得错误明细', code: ApiCode.INTERNAL_ERROR });
  const isNetwork = err.httpStatus === null;
  return (
    <Result
      status={isNetwork ? 'warning' : 'error'}
      title={title ?? (isNetwork ? '无法连接后端服务' : '数据加载失败')}
      subTitle={
        <Space direction="vertical" size={2} style={{ textAlign: 'left' }}>
          <span>{err.message}</span>
          {err.traceId ? (
            <Typography.Text type="secondary" copyable style={{ fontSize: 12 }}>
              traceId：{err.traceId}（报障时提供给技术实施组）
            </Typography.Text>
          ) : null}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            接口：{err.url ?? '-'} · 错误码：{err.code}
          </Typography.Text>
        </Space>
      }
      extra={
        onRetry ? (
          <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
            重新加载
          </Button>
        ) : null
      }
    />
  );
}

export interface EmptyStateProps {
  text?: React.ReactNode;
  action?: React.ReactNode;
  compact?: boolean;
}

export function EmptyState({ text, action, compact = false }: EmptyStateProps): React.ReactElement {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={text ?? '暂无数据'}
      style={{ padding: compact ? '16px 0' : '40px 0' }}
    >
      {action}
    </Empty>
  );
}

export interface FallbackNoticeProps {
  reason?: string | null;
  onRetry?: () => void;
  /** 补充说明，例如「当前展示的是本地演示数据」 */
  detail?: string;
}

/** 演示数据提示 —— 接口未就绪时明确告知，避免把演示数据误当真实业务数据 */
export function FallbackNotice({ reason, onRetry, detail }: FallbackNoticeProps): React.ReactElement {
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message="当前展示的是本地演示数据（后端业务接口尚未就绪）"
      description={
        <Space direction="vertical" size={2}>
          <span>
            {detail ??
              '发票、校验、流程、档案等接口正在并行开发中。接口就绪后本页会自动切换为真实数据，无需改代码。'}
          </span>
          {reason ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              接口返回：{reason}
            </Typography.Text>
          ) : null}
        </Space>
      }
      action={
        onRetry ? (
          <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
            重试
          </Button>
        ) : null
      }
    />
  );
}

export interface PageStateProps {
  loading?: boolean;
  error?: ApiError | null;
  empty?: boolean;
  emptyText?: React.ReactNode;
  emptyAction?: React.ReactNode;
  onRetry?: () => void;
  /** 表格类页面用 Skeleton 保持布局稳定 */
  skeleton?: boolean;
  rows?: number;
  children: React.ReactNode;
}

export function PageState({
  loading = false,
  error = null,
  empty = false,
  emptyText,
  emptyAction,
  onRetry,
  skeleton = false,
  rows = 6,
  children,
}: PageStateProps): React.ReactElement {
  if (loading) {
    return skeleton ? (
      <Skeleton active paragraph={{ rows }} />
    ) : (
      <div style={{ padding: '48px 0', textAlign: 'center' }}>
        <Skeleton active paragraph={{ rows: Math.min(rows, 4) }} />
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }

  if (empty) {
    return <EmptyState text={emptyText} action={emptyAction} />;
  }

  return <>{children}</>;
}

export default PageState;
